import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LogOut, User, Menu } from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const linkCls = ({ isActive }) =>
    `label-mono px-3 py-2 transition-colors ${isActive ? "text-[#002FA7]" : "text-slate-700 hover:text-[#002FA7]"}`;

  const navItems = [
    { to: "/free", label: "Test gratuito", testid: "free" },
    { to: "/simulazione", label: "Simulazione", testid: "simulation" },
    { to: "/tests", label: t("nav_tests"), testid: "tests" },
    { to: "/blog", label: "Blog", testid: "blog" },
    { to: "/guides", label: t("nav_guides"), testid: "guides" },
    { to: "/pricing", label: t("nav_pricing"), testid: "pricing" },
  ];
  if (user) navItems.push({ to: "/dashboard", label: t("nav_dashboard"), testid: "dashboard" });
  if (user?.role === "admin") navItems.push({ to: "/admin/anteprima", label: "Anteprima", testid: "admin" });

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-2 group">
          <div className="w-8 h-8 bg-[#002FA7] flex items-center justify-center">
            <span className="text-white font-display font-black text-xs">EU</span>
          </div>
          <span className="font-display font-black text-lg tracking-tighter">Logic Lab</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} data-testid={`nav-${item.testid}`} className={linkCls}>{item.label}</NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu" className="flex items-center gap-2 border border-slate-300 px-3 py-1.5 hover:border-[#002FA7]">
                <User className="w-3.5 h-3.5" />
                <span className="text-sm max-w-[140px] truncate">{user.name || user.email}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="menu-dashboard">{t("nav_dashboard")}</DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout">
                  <LogOut className="w-3.5 h-3.5 mr-2" />{t("nav_logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login"><Button variant="ghost" size="sm" data-testid="header-login-btn" className="rounded-none label-mono">{t("nav_login")}</Button></Link>
              <Link to="/register"><Button size="sm" data-testid="header-register-btn" className="rounded-none bg-[#002FA7] hover:bg-[#001d6b] label-mono">{t("nav_register")}</Button></Link>
            </>
          )}

          {/* Mobile menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" aria-label="Menu"
                className="md:hidden flex items-center justify-center w-9 h-9 border border-slate-300 hover:border-[#002FA7]">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <nav className="flex flex-col gap-1 mt-8">
                {navItems.map((item) => (
                  <NavLink key={item.to} to={item.to} data-testid={`mobile-nav-${item.testid}`}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) => `label-mono px-3 py-3 border-b border-slate-100 transition-colors ${isActive ? "text-[#002FA7]" : "text-slate-800 hover:text-[#002FA7]"}`}>
                    {item.label}
                  </NavLink>
                ))}
                {user ? (
                  <button data-testid="mobile-logout" onClick={async () => { setMobileOpen(false); await logout(); nav("/"); }}
                    className="label-mono px-3 py-3 text-left text-slate-800 hover:text-[#002FA7] flex items-center gap-2">
                    <LogOut className="w-4 h-4" />{t("nav_logout")}
                  </button>
                ) : (
                  <div className="flex flex-col gap-2 mt-4">
                    <Link to="/login" onClick={() => setMobileOpen(false)}>
                      <Button variant="outline" data-testid="mobile-login" className="w-full rounded-none label-mono">{t("nav_login")}</Button>
                    </Link>
                    <Link to="/register" onClick={() => setMobileOpen(false)}>
                      <Button data-testid="mobile-register" className="w-full rounded-none bg-[#002FA7] hover:bg-[#001d6b] label-mono">{t("nav_register")}</Button>
                    </Link>
                  </div>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
